import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { MultiselectFilterComponent } from '../../../../shared/components/multiselect-filter/multiselect-filter.component';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  debounceTime,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  findLeadsConfig,
  InquirySubmissions,
  LeadFilterTypes,
  LeadsTabConfig,
  LeadSubTabs,
  LeadTabs,
  PrelendingFormConfig,
} from '../../models/find-leads.config';
import { AuthService } from '../../../auth/services/auth.service';
import { CountPipe } from '../../../../shared/pipes/count.pipe';
import { ArcgisWebmapComponent } from '../../../../shared/components/arcgis-webmap/arcgis-webmap.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { SearchService } from '../../../search/services/search.service';
import {
  SearchFilter,
  SearchForm,
} from '../../../../shared/interfaces/search-filter';
import { LoggedInUser } from '../../../../shared/models/logged-in-user';
import { SearchResponse } from '../../../search/models/search-response';
import { SearchResultV2Component } from '../../../search/components/search-result-v2/search-result-v2.component';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import {
  Company,
  ContainerSasUrl,
} from '../../../../shared/interfaces/company';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LeadsCriteria } from '../../../outreach/models/leads-criteria.model';
import { LeadsCriteriaService } from '../../../outreach/services/leads-criteria.service';
import { SearchWncsbiComponent } from '../../../search/components/search-wncsbi/search-wncsbi.component';
import { AdvancedFiltersBuilder } from '../../../search/models/advanced-filters';
import { SearchDataAxleComponent } from '../../../search/components/search-data-axle/search-data-axle.component';
import { ManageConnectionService } from '../../../manage-connection-group/services/manage-connection.service';
import { Auth0Connection } from '../../../../shared/models/auth0-connection';
import { FilterComponent } from '../../../../shared/components/filter/filter.component';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { PublicLinkService } from '../../../outreach/services/public-link.service';
import { OutreachLink } from '../../../outreach/models/outreach-link';
import { SourceTypes } from '../../../search/models/search-source-types';
import { AddBusinessRecordComponent } from '../../../search/components/add-business-record/add-business-record.component';
import { RecordsByType } from '../../../search/models/record-count';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { AddressService } from '../../../../shared/services/address/address.service';
import { State, County, City } from '../../../../shared/models/address';
import { SearchableFilterComponent } from '../../../../shared/components/searchable-filter/searchable-filter.component';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-find-leads-v2',
  templateUrl: './find-leads-v2.component.html',
  styleUrls: ['./find-leads-v2.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SearchInputComponent,
    MultiselectFilterComponent,
    NgTemplateOutlet,
    CountPipe,
    ArcgisWebmapComponent,
    SvgIconComponent,
    SearchResultV2Component,
    MultiselectFilterComponent,
    SearchWncsbiComponent,
    SearchDataAxleComponent,
    FilterComponent,
    AddBusinessRecordComponent,
    SkeletonComponent,
    SearchableFilterComponent,
  ],
})
export class FindLeadsV2Component extends MenuComponentBase implements OnInit {
  private authService = inject(AuthService);
  private commonService = inject(CommonService);

  private searchService = inject(SearchService);
  private imageService = inject(ImageService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private leadsCriteriaService = inject(LeadsCriteriaService);
  private manageConnectionService = inject(ManageConnectionService);
  private publicLinkService = inject(PublicLinkService);
  private addressService = inject(AddressService);

  private searchQuerySubject = new Subject<string>();
  searchQuery = '';

  readonly LeadTabs = LeadTabs;
  readonly LeadSubTabs = LeadSubTabs;
  readonly LeadFilterTypes = LeadFilterTypes;
  readonly prelendingFormConfig = PrelendingFormConfig;
  readonly inquirySubmissions = InquirySubmissions;

  config: LeadsTabConfig[] = [];

  subTabs: LeadsTabConfig[] = [];

  selectedTab: LeadsTabConfig | null = null;
  selectedSubTab: LeadsTabConfig | null = null;

  currentPage = 1;
  pageSize = 50;
  totalPages = 0;

  totalResults = 0;
  searchResultsList!: SearchResponse;
  searchResultFilter: SearchFilter = {};
  isSearchResultsLoading = false;
  hasError = false;

  loggedInUserInfo: LoggedInUser | null = null;
  containerSasUrl!: ContainerSasUrl;

  isSearchPage = false;

  deeprootsCount!: RecordsByType;

  leadsCriteriaList: LeadsCriteria[] = [];
  matchedCriteriaOptions: { option: string; value: string }[] = [];
  matchedCriteriaSelection: string[] = [];

  preLendingFormSelectedTab: LeadsTabConfig | null = null;
  advanceFilterCount = 0;

  isAdvancedFilterVisible = false;

  inquirySelectedTab: LeadsTabConfig | null = null;
  connections: Auth0Connection[] = [];
  outreachLinks: OutreachLink[] = [];
  deeprootsCdfiFilter = '';
  deeprootsLinkFilter = '';
  deeprootsCdfiOptions: { option: string; value: string }[] = [
    { option: 'Select CDFI', value: '' },
  ];
  deeprootsLinkOptions: { option: string; value: string }[] = [
    { option: 'Select Link', value: '' },
  ];

  // Address filter properties
  states: State[] = [];
  counties: County[] = [];
  cities: City[] = [];
  stateFilter = '';
  countyFilter = '';
  cityFilter = '';
  stateOptions: { option: string; value: string }[] = [
    { option: 'Select State', value: '' },
  ];
  countyOptions: { option: string; value: string }[] = [
    { option: 'Select County', value: '' },
  ];
  cityOptions: { option: string; value: string }[] = [
    { option: 'Select City', value: '' },
  ];

  filterRecordsOptions: { option: string; value: string }[] = [];
  filterBy = 'all';

  recordId: string | null = null;

  @ViewChild(SearchWncsbiComponent)
  searchWncsbiComponent!: SearchWncsbiComponent;

  @ViewChild(ArcgisWebmapComponent)
  arcgisWebmapComponent!: ArcgisWebmapComponent;

  get isAdminUser(): boolean {
    return this.authService.hasPermission(PermissionCode.ADMIN_PRIVILEGES);
  }

  get hasManagePublicLinksPermission(): boolean {
    return this.authService.hasPermission(PermissionCode.MANAGE_PUBLIC_LINKS);
  }

  checkPermission(permissionCode: PermissionCode | undefined): boolean {
    if (permissionCode) {
      return this.authService.hasPermission(permissionCode);
    }
    return true;
  }

  private setSourceFilter(): void {
    if (this.selectedTab?.value === LeadTabs.DEEPROOTS) {
      delete this.searchResultFilter.sources;
      return;
    }

    this.searchResultFilter.sources = this.selectedTab?.source;

    delete this.searchResultFilter.deeproots_inquiries;
  }

  private resetSearchResults() {
    this.isSearchResultsLoading = false;

    this.searchResultsList = {
      count: 0,
      results: [],
    };
  }

  private getUpdatedFilter(): SearchFilter {
    let updatedFilter = {
      ...this.searchResultFilter,
    };

    delete updatedFilter.payload;

    if (!this.isAdminUser) {
      delete updatedFilter.created_by_org;
    }

    if (
      this.inquirySelectedTab?.value === LeadSubTabs.SME &&
      this.selectedTab?.value === LeadTabs.DEEPROOTS &&
      this.selectedSubTab?.value === LeadSubTabs.INQUIRIES
    ) {
      updatedFilter = { ...updatedFilter, sources: LeadSubTabs.SME };
    } else {
      updatedFilter = { ...updatedFilter, ...this.searchResultFilter.payload };
    }

    return updatedFilter;
  }

  getDeepRootsCount(key: string): number {
    return this.deeprootsCount[key as keyof RecordsByType];
  }

  override ngOnInit() {
    super.ngOnInit();

    this.searchQuerySubject.pipe(debounceTime(300)).subscribe((query) => {
      this.applySearch(query);
    });

    this.config = findLeadsConfig.filter((tab) => {
      if (tab.permissionCode) {
        return this.authService.hasPermission(tab.permissionCode);
      }
      return false;
    });

    this.loggedInUserInfo = this.authService.getLoggedInUserInfo();

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        if (this.router.url.includes('/search') && !this.isSearchPage) {
          this.handleSearchPage(queryParams);
        }
      });

    if (this.isSearchPage) return;

    this.initializeSearchFilter();

    if (this.selectedTab?.value === LeadTabs.VLF) {
      this.fetchVlfStatus();
    }

    this.updateQueryParams();

    this.fetchRecordCount();

    this.renderFilters();

    this.triggerInitialData();
  }

  initializeSearchFilter() {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    this.selectedTab =
      this.config.find((tab) => tab.value === queryParams['tab']) ??
      this.config[0];

    this.subTabs = this.selectedTab.subTabs;

    this.selectedSubTab =
      this.subTabs.find((subTab) => subTab.value === queryParams['subtab']) ??
      this.subTabs[0] ??
      null;

    if (queryParams['inquiry_tab']) {
      this.inquirySelectedTab =
        this.inquirySubmissions.find(
          (tab) => tab.value === queryParams['inquiry_tab'],
        ) ?? this.inquirySubmissions[0];
    } else if (queryParams['submitted_by_link_or_qr']) {
      this.inquirySelectedTab =
        this.inquirySubmissions.find(
          (tab) => tab.value === LeadSubTabs.BY_LINK,
        ) ?? null;
    } else {
      this.inquirySelectedTab = this.inquirySubmissions[0];
    }

    if (this.inquirySelectedTab?.value === LeadSubTabs.BY_LINK) {
      this.searchResultFilter.submitted_by_link_or_qr = true;
    }

    if (queryParams['created_by_org']) {
      this.deeprootsCdfiFilter = queryParams['created_by_org'];
      this.searchResultFilter.created_by_org = queryParams['created_by_org'];
    }

    if (queryParams['created_by_link']) {
      this.deeprootsLinkFilter = queryParams['created_by_link'];
      this.searchResultFilter.created_by_link = queryParams['created_by_link'];
    }

    if (queryParams['primary_name']) {
      this.searchQuery = queryParams['primary_name'];
    }

    if (queryParams['recordId']) {
      this.recordId = queryParams['recordId'];
    }

    if (this.selectedTab?.value === LeadTabs.DEEPROOTS) {
      delete this.searchResultFilter.sources;
    } else {
      this.searchResultFilter.sources = this.selectedTab.source;
      delete this.searchResultFilter.deeproots_inquiries;
    }

    this.searchResultFilter.payload = this.selectedSubTab?.payload;
  }

  handleSearchPage(queryParams: Params) {
    this.isSearchPage = true;

    this.searchQuery = queryParams['primary_name'];

    this.searchResultFilter.primary_name = queryParams['primary_name'];

    this.searchResultFilter.sources = queryParams['sources'];

    this.recordId = queryParams['recordId'] ?? null;

    this.filterRecordsOptions = [{ option: 'All', value: 'all' }];

    const filterRecordOptions = SourceTypes.filter(
      (s) => s.value !== 'ACC_ALL_SOURCES' && s.value !== 'DATA_AXLE',
    ).map((source) => ({ option: source.label, value: source.value }));

    this.filterRecordsOptions = [
      ...this.filterRecordsOptions,
      ...filterRecordOptions,
    ];

    const sources = queryParams['sources']
      ? queryParams['sources'].split(',')
      : [];

    const matchedOption = this.filterRecordsOptions.find(
      (option) => option.value.toLowerCase() === sources[0]?.toLowerCase(),
    );

    this.filterBy =
      sources.length > 1 ? 'all' : (matchedOption?.value ?? 'all');

    this.triggerInitialData();
  }

  triggerInitialData() {
    this.isSearchResultsLoading = true;

    forkJoin({
      sasUrl: this.imageService.getContainerSasUrl(),
      searchResults:
        this.selectedTab?.value === LeadTabs.DATA_AXLE
          ? of({
              count: 0,
              results: [],
            })
          : this.loadIntialSearchResults(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ sasUrl, searchResults }) => {
          this.hasError = false;

          this.containerSasUrl = sasUrl;

          this.updateSearchResults(searchResults);
        },
        error: () => {
          this.hasError = true;
          this.resetSearchResults();
        },
      });
  }

  loadIntialSearchResults() {
    if (
      this.selectedTab?.value === LeadTabs.VLF &&
      (!this.isVlfOptedIn || this.isVlfStatusLoading)
    ) {
      return of({ count: 0, results: [] });
    }

    this.searchQuery = this.searchQuery?.trim();

    if (this.searchQuery !== null) {
      if (
        this.selectedTab?.value === LeadTabs.WNCSBI ||
        this.selectedTab?.value === LeadTabs.DATA_AXLE
      ) {
        this.applySharedBusinessNameToSourceFilter(this.searchQuery);
        this.currentPage = 1;

        if (
          this.selectedTab?.value === LeadTabs.DATA_AXLE &&
          !this.searchResultFilter.data_axle_search?.advanced_filters?.length
        ) {
          return of({ count: 0, results: [] });
        }
      }
    }

    if (this.searchQuery) {
      this.searchResultFilter.primary_name = this.searchQuery;
    } else {
      delete this.searchResultFilter.primary_name;
    }

    this.currentPage = 1;
    return this.searchService.getCompanies(
      this.getUpdatedFilter(),
      this.currentPage,
      this.pageSize,
      true,
    );
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;

    if (query.length === 0 || query.length >= 2) {
      this.searchQuerySubject.next(query);
    }
  }

  onTabChange(tab: LeadsTabConfig): void {
    if (this.selectedTab?.value === tab.value) {
      return;
    }

    if (this.selectedSubTab?.value === LeadSubTabs.INQUIRIES) {
      this.clearInquiryFilters();
    }

    this.selectedTab = tab;
    this.subTabs = tab.subTabs;
    this.selectedSubTab = tab.subTabs?.[0] ?? null;

    this.currentPage = 1;
    this.searchQuery = '';
    this.hasError = false;
    this.isAdvancedFilterVisible = false;
    this.advanceFilterCount = 0;
    this.searchResultFilter = {};
    this.matchedCriteriaSelection = [];

    this.setSourceFilter();

    this.searchResultFilter.payload = this.selectedSubTab?.payload;

    // Clear map points when tab changes
    this.arcgisWebmapComponent?.clearMapPoints();

    if (tab.value === LeadTabs.VLF) {
      this.fetchVlfStatus();
    }

    this.updateQueryParams();
    this.fetchRecordCount();
    this.renderFilters();
    this.loadSearchResults(true);
  }

  onSubTabChange(subTab: LeadsTabConfig): void {
    if (this.selectedSubTab?.value === subTab.value) {
      return;
    }

    if (this.selectedSubTab?.value === LeadSubTabs.INQUIRIES) {
      this.clearInquiryFilters();
    }

    this.selectedSubTab = subTab;

    this.currentPage = 1;
    this.searchQuery = '';
    this.hasError = false;
    this.matchedCriteriaSelection = [];

    delete this.searchResultFilter.primary_name;
    delete this.searchResultFilter.payload;

    if (subTab?.payload) {
      this.searchResultFilter.payload = subTab.payload;
    }

    // Clear map points when subtab changes
    this.arcgisWebmapComponent?.clearMapPoints();

    this.updateQueryParams();

    this.renderFilters();

    this.loadSearchResults(true);
  }

  clearInquiryFilters() {
    delete this.searchResultFilter.created_by_org;
    delete this.searchResultFilter.created_by_link;
    delete this.searchResultFilter.submitted_by_link_or_qr;
    delete this.searchResultFilter.state;
    delete this.searchResultFilter.county;
    delete this.searchResultFilter.city;
    this.inquirySelectedTab = this.inquirySubmissions[0];
    this.deeprootsCdfiFilter = '';
    this.deeprootsLinkFilter = '';
    this.resetAddressFilters();
  }

  updateQueryParams(): void {
    const queryParams: Record<string, string> = {};

    if (this.selectedTab?.value) {
      queryParams['tab'] = this.selectedTab.value;
    }

    if (this.selectedSubTab?.value) {
      queryParams['subtab'] = this.selectedSubTab.value;
    }

    if (
      this.selectedTab?.value === LeadTabs.DEEPROOTS &&
      this.selectedSubTab?.value === LeadSubTabs.INQUIRIES &&
      this.inquirySelectedTab?.value
    ) {
      queryParams['inquiry_tab'] = this.inquirySelectedTab.value;
    }

    if (this.searchResultFilter.submitted_by_link_or_qr) {
      queryParams['submitted_by_link_or_qr'] = 'true';
    }

    if (this.searchResultFilter.created_by_org) {
      queryParams['created_by_org'] = this.searchResultFilter.created_by_org;
    }

    if (this.searchResultFilter.created_by_link) {
      queryParams['created_by_link'] = this.searchResultFilter.created_by_link;
    }

    if (this.searchQuery) {
      queryParams['primary_name'] = this.searchQuery;
    }

    if (this.recordId !== null) {
      queryParams['recordId'] = this.recordId;
    }

    if (this.isSearchPage && this.searchResultFilter.sources) {
      queryParams['sources'] = this.searchResultFilter.sources;
    }

    this.arcgisWebmapComponent?.clearMapPoints();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        tab: null,
        subtab: null,
        inquiry_tab: null,
        ...queryParams,
      },
    });
  }

  fetchRecordCount() {
    if (this.selectedTab && this.selectedTab.value === LeadTabs.DEEPROOTS) {
      this.searchService
        .getRecordCount()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.deeprootsCount = res.count;
        });
    }
  }

  renderFilters(): void {
    if (!this.selectedSubTab?.filters?.length) {
      return;
    }

    this.selectedSubTab.filters.forEach((filter) => {
      switch (filter) {
        case LeadFilterTypes.MATCHED_CRITERIA:
          if (!this.leadsCriteriaList.length) {
            this.getLeadsCriteria();
          }
          break;

        case LeadFilterTypes.PRE_LENDING_FORM:
          this.updatePrelendingForm();
          break;

        case LeadFilterTypes.INQUIRY_SUBMISSIONS:
          this.updateInquirySubmission();
          break;
      }
    });
  }

  getLeadsCriteria() {
    this.leadsCriteriaService
      .getLeadsCriteria(undefined, true, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (criteria) => {
          this.leadsCriteriaList = criteria;
          this.matchedCriteriaOptions = this.leadsCriteriaList.map(
            (criteria) => ({
              option: criteria.name,
              value: criteria.id.toString(),
            }),
          );
        },
        error: (error) => {
          console.error('Error loading leads criteria:', error);
          this.matchedCriteriaSelection = [];
        },
      });
  }

  updatePrelendingForm() {
    this.preLendingFormSelectedTab =
      this.preLendingFormSelectedTab ?? this.prelendingFormConfig[0];

    if (this.preLendingFormSelectedTab?.value !== LeadSubTabs.ALL) {
      this.searchResultFilter.payload = {
        ...this.searchResultFilter.payload,
        pre_lending_form: this.preLendingFormSelectedTab?.value === 'true',
      };
    } else {
      delete this.searchResultFilter.payload?.['pre_lending_form'];
    }
  }

  updateInquirySubmission() {
    this.inquirySelectedTab =
      this.inquirySelectedTab ?? this.inquirySubmissions[0];

    if (
      this.inquirySelectedTab?.value === LeadSubTabs.BY_LINK &&
      (this.isAdminUser || this.hasManagePublicLinksPermission) &&
      this.connections.length === 0
    ) {
      this.getConnections().pipe(takeUntil(this.destroy$)).subscribe();
    }

    // Load states for address filters if not already loaded
    if (this.states.length === 0) {
      this.loadStates();
    }
  }

  private getUserConnectionId(): string {
    if (!this.loggedInUserInfo) {
      this.loggedInUserInfo = this.authService.getLoggedInUserInfo();
    }
    if (!this.connections.length) {
      return '';
    }

    const userConnection = this.connections.find(
      (con) => con.connection_name === this.loggedInUserInfo?.connection,
    );

    return userConnection
      ? userConnection.connection_id.toString()
      : (this.connections[0]?.connection_id?.toString() ?? '');
  }

  getConnections() {
    return this.manageConnectionService.getConnectionsFromDB().pipe(
      tap((response: Auth0Connection[]) => {
        this.connections = response;

        const cdfiFilterOptions = this.connections.map((con) => ({
          option: con.connection_display_name ?? con.connection_name,
          value: con.connection_id.toString(),
        }));

        this.deeprootsCdfiOptions = [
          { option: 'Select CDFI', value: '' },
          ...cdfiFilterOptions,
        ];

        this.deeprootsCdfiFilter =
          this.deeprootsCdfiFilter !== ''
            ? this.deeprootsCdfiFilter
            : this.getUserConnectionId();

        if (
          this.searchResultFilter.submitted_by_link_or_qr &&
          !this.searchResultFilter.created_by_org &&
          this.isAdminUser
        ) {
          this.searchResultFilter.created_by_org = this.deeprootsCdfiFilter;
          this.updateQueryParams();
        }

        this.searchResultFilter.submitted_by_link_or_qr = true;
        if (this.isAdminUser) {
          this.searchResultFilter.created_by_org = this.deeprootsCdfiFilter;
        }
      }),
      switchMap(() => {
        if (!this.deeprootsCdfiFilter) {
          return of(null);
        }

        return this.getOutreachLinks(this.deeprootsCdfiFilter);
      }),
    );
  }

  getOutreachLinks(connectionId?: string) {
    return this.publicLinkService
      .getAllPublicOutreachLinks(1, 100, undefined, undefined, connectionId)
      .pipe(
        tap((response) => {
          this.outreachLinks = response.results;

          this.deeprootsLinkOptions = [
            { option: 'Select Link', value: '' },
            ...this.outreachLinks.map((l) => ({
              option: l.name,
              value: l.unique_id,
            })),
          ];
        }),
      );
  }

  onMatchedCriteriaFilterChange(selectedValues: string[]): void {
    this.matchedCriteriaSelection = selectedValues;
    this.searchResultFilter.payload = {
      ...this.searchResultFilter.payload,
      matching_criteria: selectedValues,
    };
    this.arcgisWebmapComponent?.clearMapPoints();
    this.loadSearchResults(true);
  }

  onPreLendingForm(tab: LeadsTabConfig) {
    if (this.preLendingFormSelectedTab?.value === tab.value) {
      return;
    }
    this.arcgisWebmapComponent?.clearMapPoints();
    this.preLendingFormSelectedTab = tab;
    if (tab.value !== LeadSubTabs.ALL) {
      this.searchResultFilter.payload = {
        ...this.searchResultFilter.payload,
        pre_lending_form: tab.value === 'true',
      };
    } else {
      delete this.searchResultFilter.payload?.['pre_lending_form'];
    }
    this.loadSearchResults(true);
  }

  onInquirySubmissions(tab: LeadsTabConfig) {
    if (this.inquirySelectedTab?.value === tab.value) {
      return;
    }
    this.inquirySelectedTab = tab;

    if (tab.value !== LeadSubTabs.BY_LINK) {
      delete this.searchResultFilter.submitted_by_link_or_qr;
      delete this.searchResultFilter.created_by_org;
      delete this.searchResultFilter.created_by_link;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let request$: Observable<any> = of(null);
    if (tab.value === LeadSubTabs.BY_LINK) {
      delete this.searchResultFilter.state;
      delete this.searchResultFilter.county;
      delete this.searchResultFilter.city;
      this.resetAddressFilters();
      this.arcgisWebmapComponent?.clearMapPoints();

      this.searchResultFilter.submitted_by_link_or_qr = true;

      if (this.isAdminUser || this.hasManagePublicLinksPermission) {
        if (this.connections.length === 0) {
          request$ = this.getConnections();
        } else {
          this.deeprootsCdfiFilter = this.getUserConnectionId();
          if (this.isAdminUser) {
            this.searchResultFilter.created_by_org = this.deeprootsCdfiFilter;
          }
          request$ = this.getOutreachLinks(this.deeprootsCdfiFilter);
        }
      }
    }

    request$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (tab.value !== LeadSubTabs.BY_LINK) {
        this.deeprootsCdfiFilter = '';
        this.deeprootsLinkFilter = '';
      }

      this.updateQueryParams();
      this.loadSearchResults();
    });
  }

  onDeeprootsCdfiFilterChanged(value: string): void {
    this.deeprootsCdfiFilter = value;
    this.deeprootsLinkFilter = '';
    this.arcgisWebmapComponent?.clearMapPoints();
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: {
          created_by_org: value || null,
          created_by_link: null,
        },
        queryParamsHandling: 'merge',
      })
      .then(() => {
        this.getOutreachLinks(value)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.searchResultFilter.submitted_by_link_or_qr = true;
            if (value) {
              this.searchResultFilter.created_by_org = value;
            } else {
              delete this.searchResultFilter.created_by_org;
            }
            delete this.searchResultFilter.created_by_link;
            this.loadSearchResults();
          });
      });
  }

  onDeeprootsLinkFilterChanged(value: string): void {
    this.deeprootsLinkFilter = value;
    this.arcgisWebmapComponent?.clearMapPoints();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        created_by_link: value || null,
      },
      queryParamsHandling: 'merge',
    });
    if (value) {
      this.searchResultFilter.created_by_link = value;
    } else {
      delete this.searchResultFilter.created_by_link;
    }
    this.loadSearchResults();
  }

  clearDeeprootsFilters(): void {
    this.deeprootsLinkFilter = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        submitted_by_link_or_qr:
          this.inquirySelectedTab?.value === 'link' ? 'true' : null,
        created_by_org: this.deeprootsCdfiFilter ?? null,
        created_by_link: null,
      },
      queryParamsHandling: 'merge',
    });
    delete this.searchResultFilter.created_by_link;
    delete this.searchResultFilter.state;
    delete this.searchResultFilter.county;
    delete this.searchResultFilter.city;
    this.resetAddressFilters();
    this.loadSearchResults();
  }

  private loadStates(): void {
    this.addressService
      .getStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((states) => {
        this.states = states;
        this.stateOptions = [
          { option: 'Select State', value: '' },
          ...states.map((s) => ({ option: s.name, value: s.id.toString() })),
        ];
      });
  }

  onStateFilterChanged(value: string): void {
    this.stateFilter = value;
    this.countyFilter = '';
    this.cityFilter = '';
    this.counties = [];
    this.cities = [];
    this.countyOptions = [{ option: 'Select County', value: '' }];
    this.cityOptions = [{ option: 'Select City', value: '' }];

    delete this.searchResultFilter.county;
    delete this.searchResultFilter.city;

    if (value) {
      const state = this.states.find((s) => s.id.toString() === value);
      this.searchResultFilter.state = state?.name;

      this.addressService
        .getCitiesAndCountiesByStateIds([value])
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          this.counties = data.counties;
          this.cities = data.cities;
          this.countyOptions = [
            { option: 'Select County', value: '' },
            ...data.counties.map((c) => ({
              option: c.name,
              value: c.id.toString(),
            })),
          ];
          this.cityOptions = [
            { option: 'Select City', value: '' },
            ...data.cities.map((c) => ({
              option: c.name,
              value: c.id.toString(),
            })),
          ];
        });
    } else {
      delete this.searchResultFilter.state;
    }

    this.arcgisWebmapComponent?.clearMapPoints();
    this.loadSearchResults();
  }

  onCountyFilterChanged(value: string): void {
    this.countyFilter = value;

    if (value) {
      const county = this.counties.find((c) => c.id.toString() === value);
      this.searchResultFilter.county = county?.name;
    } else {
      delete this.searchResultFilter.county;
    }

    this.arcgisWebmapComponent?.clearMapPoints();
    this.loadSearchResults();
  }

  onCityFilterChanged(value: string): void {
    this.cityFilter = value;

    if (value) {
      const city = this.cities.find((c) => c.id.toString() === value);
      this.searchResultFilter.city = city?.name;
    } else {
      delete this.searchResultFilter.city;
    }

    this.arcgisWebmapComponent?.clearMapPoints();
    this.loadSearchResults();
  }

  private resetAddressFilters(): void {
    this.stateFilter = '';
    this.countyFilter = '';
    this.cityFilter = '';
    this.counties = [];
    this.cities = [];
    this.countyOptions = [{ option: 'Select County', value: '' }];
    this.cityOptions = [{ option: 'Select City', value: '' }];
  }

  clearAddressFilters(): void {
    delete this.searchResultFilter.state;
    delete this.searchResultFilter.county;
    delete this.searchResultFilter.city;
    this.resetAddressFilters();
    this.arcgisWebmapComponent?.clearMapPoints();
    this.loadSearchResults();
  }

  onCompanyDeleted(uniqueId: string) {
    if (this.searchResultsList.results.length > 0) {
      const filteredResults = this.searchResultsList.results.filter(
        (company) => company.unique_id !== uniqueId,
      );
      this.searchResultsList = {
        count: this.totalResults,
        results: filteredResults,
      };
    }
  }

  onCompanyDetailsUpdated(updatedCompany: Company) {
    if (this.searchResultsList.results.length > 0) {
      const index = this.searchResultsList.results.findIndex(
        (c) => c.unique_id === updatedCompany.unique_id,
      );
      if (index !== -1) {
        this.searchResultsList.results[index] = updatedCompany;
      }
    }
  }

  OnLeadAssign(updatedCompany: Company | null) {
    if (!updatedCompany) return;

    if (this.searchResultsList?.results?.length > 0) {
      const index = this.searchResultsList.results.findIndex(
        (c) => c.unique_id === updatedCompany.unique_id,
      );
      if (index !== -1) {
        this.searchResultsList.results[index] = {
          ...this.searchResultsList.results[index],
          ...updatedCompany,
          assigned_to: updatedCompany.assigned_to,
          assignees: updatedCompany.assignees,
          leadId: updatedCompany.leadId,
        };
        this.searchResultsList = {
          ...this.searchResultsList,
          results: [...this.searchResultsList.results],
        };
      }
    }
  }

  onShowInMap(selectedCompanies: Company[]): void {
    this.arcgisWebmapComponent.showMapPoints(selectedCompanies);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadSearchResults(true);
  }

  toggleAdvancedFilters() {
    this.isAdvancedFilterVisible = !this.isAdvancedFilterVisible;
    this.searchWncsbiComponent.isAdvancedSearchVisible = true;
    this.searchWncsbiComponent.advancedSearchVisible = true;
  }

  filterCountChanged(count: number): void {
    this.advanceFilterCount = count;
    if (
      this.selectedTab?.value === LeadTabs.DATA_AXLE &&
      this.advanceFilterCount === 0
    ) {
      this.searchResultsList = {
        count: 0,
        results: [],
      };
    }
  }

  onResetAdvanceSearch() {
    this.searchQuery = '';
    this.searchResultFilter = {};
    this.searchResultFilter.sources = this.selectedTab?.source;
    this.searchResultFilter.payload = this.selectedSubTab?.payload;
    this.currentPage = 1;
    this.preLendingFormSelectedTab = this.prelendingFormConfig[0];
    this.loadSearchResults();
  }

  onWNCSBIAdvanceSearch(
    advancedSearchFilters: SearchForm,
    source: string,
  ): void {
    this.isAdvancedFilterVisible = false;
    const sharedBusinessName = this.searchQuery.trim();
    this.preLendingFormSelectedTab = this.prelendingFormConfig[0];
    this.selectedSubTab = this.subTabs[0];
    this.searchResultFilter = {
      ...this.searchResultFilter,
      advanced_search: true,
      advanced_search_filter: {
        ...advancedSearchFilters,
        ...(sharedBusinessName ? { company_name: sharedBusinessName } : {}),
      },
      sources: source,
    };

    delete this.searchResultFilter.primary_name;
    delete this.searchResultFilter.data_axle_search;

    this.currentPage = 1;

    this.loadSearchResults();
  }

  onDataAxleAdvancedSearch(
    dataAxleSearchFilters: AdvancedFiltersBuilder,
    source: string,
  ): void {
    const sharedBusinessName = this.searchQuery.trim();
    const advancedFilters = [
      ...(dataAxleSearchFilters.advanced_filters || []).filter(
        (filter) => filter.attribute !== 'name',
      ),
    ];

    if (sharedBusinessName) {
      advancedFilters.unshift({
        attribute: 'name',
        relation: 'matches',
        value: sharedBusinessName,
      });
    }

    this.searchResultFilter = {
      ...this.searchResultFilter,
      advanced_search: true,
      data_axle_search: {
        connective: dataAxleSearchFilters.connective,
        advanced_filters: advancedFilters,
      },
      sources: source,
    };
    delete this.searchResultFilter.primary_name;
    delete this.searchResultFilter.advanced_search_filter;
    this.currentPage = 1;
    this.loadSearchResults();
  }

  getFilterableKeys(filter: SearchFilter): string[] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { self_created, self_org_created, ...rest } = filter;
    return Object.keys(rest);
  }

  refreshSource(source: string): void {
    const queryParams: Record<string, unknown> = {};

    const sources =
      this.searchResultFilter.sources?.split(',').filter((s) => s !== source) ??
      [];

    if (sources.length > 0) {
      queryParams['sources'] = sources.join(',');
      this.searchResultFilter.sources = sources.join(',');
    } else {
      queryParams['sources'] = null;
      delete this.searchResultFilter.sources;
    }

    const matchedOption = this.filterRecordsOptions.find(
      (option) => option.value.toLowerCase() === sources[0]?.toLowerCase(),
    );

    this.filterBy =
      sources.length > 1 ? 'all' : (matchedOption?.value ?? 'all');

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
    });

    this.loadSearchResults(true);
  }

  refreshSearch(removedParam: string): void {
    const queryParams: Record<string, unknown> = {};
    queryParams[removedParam] = null;
    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
    this.searchQuery = '';
    delete this.searchResultFilter.primary_name;
    this.loadSearchResults(true);
  }

  onAllRecordsFilterChange(value: string): void {
    this.filterBy = value;
    const updatedFilter = { ...this.searchResultFilter };
    updatedFilter.primary_name = this.searchQuery;
    if (value !== 'all') {
      updatedFilter.sources = value;
    } else {
      updatedFilter.sources = 'PUBLIC,CUSTOM_BUSINESS_RECORDS,WNCSBI_APPS';
    }
    this.searchResultFilter = updatedFilter;
    const queryParams: Record<string, string> = {};
    this.arcgisWebmapComponent?.clearMapPoints();
    queryParams['primary_name'] = this.searchQuery;
    queryParams['sources'] = updatedFilter.sources;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: queryParams,
    });

    this.loadSearchResults(true);
  }

  private applySharedBusinessNameToSourceFilter(query: string): void {
    const normalizedQuery = query.trim();

    if (this.selectedTab?.value === LeadTabs.WNCSBI) {
      const advancedSearchFilter = {
        ...(this.searchResultFilter.advanced_search_filter || {}),
      };

      if (normalizedQuery) {
        advancedSearchFilter.company_name = normalizedQuery;
      } else {
        delete advancedSearchFilter.company_name;
      }

      this.searchResultFilter = {
        ...this.searchResultFilter,
        advanced_search: Object.keys(advancedSearchFilter).length > 0,
        advanced_search_filter:
          Object.keys(advancedSearchFilter).length > 0
            ? advancedSearchFilter
            : undefined,
      };

      delete this.searchResultFilter.primary_name;
      delete this.searchResultFilter.data_axle_search;
      return;
    }

    if (this.selectedTab?.value === LeadTabs.DATA_AXLE) {
      const existingDataAxleSearch = this.searchResultFilter.data_axle_search;
      const advancedFilters = [
        ...(existingDataAxleSearch?.advanced_filters || []).filter(
          (filter) => filter.attribute !== 'name',
        ),
      ];

      if (normalizedQuery) {
        advancedFilters.unshift({
          attribute: 'name',
          relation: 'matches',
          value: normalizedQuery,
        });
      }

      this.searchResultFilter = {
        ...this.searchResultFilter,
        sources: 'DATA_AXLE',
        advanced_search: advancedFilters.length > 0,
        data_axle_search: advancedFilters.length
          ? {
              connective: existingDataAxleSearch?.connective || 'and',
              advanced_filters: advancedFilters,
            }
          : undefined,
      };

      delete this.searchResultFilter.primary_name;
      delete this.searchResultFilter.advanced_search_filter;
    }
  }

  private applySearch(query: string): void {
    const normalizedQuery = query.trim();
    if (normalizedQuery && normalizedQuery.length < 2) {
      return;
    }
    this.updateQueryParams();
    if (
      this.selectedTab?.value === LeadTabs.WNCSBI ||
      this.selectedTab?.value === LeadTabs.DATA_AXLE
    ) {
      this.applySharedBusinessNameToSourceFilter(normalizedQuery);
      this.currentPage = 1;

      if (
        this.selectedTab?.value === LeadTabs.DATA_AXLE &&
        !this.searchResultFilter.data_axle_search?.advanced_filters?.length
      ) {
        this.totalResults = 0;
        this.searchResultsList = { count: 0, results: [] };
        return;
      }

      this.loadSearchResults(true);
      return;
    }

    const updatedFilter = { ...this.searchResultFilter };

    if (normalizedQuery) {
      updatedFilter.primary_name = normalizedQuery;
    } else {
      delete updatedFilter.primary_name;
    }

    this.searchResultFilter = updatedFilter;
    this.currentPage = 1;
    this.loadSearchResults(true);
  }

  updateSearchResults(response: SearchResponse) {
    this.totalResults = response?.count || 0;

    this.totalPages = Math.ceil(this.totalResults / this.pageSize);

    const results = response?.results || [];

    const isDeeprootsInquiries =
      !!this.searchResultFilter.deeproots_inquiries ||
      !!this.searchResultFilter.payload?.['deeproots_inquiries'] ||
      (this.selectedTab?.value === LeadTabs.DEEPROOTS &&
        this.selectedSubTab?.value === LeadSubTabs.INQUIRIES);

    const shouldSortByCurrentUser = !isDeeprootsInquiries;

    const sortedResults = shouldSortByCurrentUser
      ? [...results].sort((a, b) => {
          const aCreatedByUser =
            a.created_by_user?.username === this.loggedInUserInfo?.username;

          const bCreatedByUser =
            b.created_by_user?.username === this.loggedInUserInfo?.username;

          return (bCreatedByUser ? 1 : 0) - (aCreatedByUser ? 1 : 0);
        })
      : results;

    this.searchResultsList = {
      count: this.totalResults,
      results: sortedResults,
    };

    this.isSearchResultsLoading = false;
  }

  loadSearchResults(skipLoader = false): void {
    if (
      this.selectedTab?.value === LeadTabs.DATA_AXLE &&
      !this.searchResultFilter.data_axle_search
    ) {
      this.resetSearchResults();
      return;
    }

    if (
      this.selectedTab?.value === LeadTabs.VLF &&
      (!this.isVlfOptedIn || this.isVlfStatusLoading)
    ) {
      this.resetSearchResults();
      return;
    }

    this.isSearchResultsLoading = true;
    this.hasError = false;

    this.searchService
      .getCompanies(
        this.getUpdatedFilter(),
        this.currentPage,
        this.pageSize,
        skipLoader,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.updateSearchResults(response);
        },
        error: () => {
          this.hasError = true;
          this.isSearchResultsLoading = false;

          this.searchResultsList = {
            count: 0,
            results: [],
          };
        },
      });
  }

  onCustomBusinessAdded() {
    this.loadSearchResults(true);
  }

  onViewDetails(id: string | null) {
    this.recordId = id;
    this.updateQueryParams();
  }

  refreshSearchResults(value: boolean) {
    if (
      value &&
      this.selectedTab?.value === LeadTabs.VLF &&
      this.isVlfOptedIn
    ) {
      this.triggerInitialData();
    }
  }

  get hasVlfPermission(): boolean {
    return this.authService.hasPermission(PermissionCode.ACCESS_VLF);
  }

  isVlfOptedIn = false;
  isVlfStatusLoading = true;
  isVlfActionLoading = false;
  showVlfInfo = false;

  fetchVlfStatus(): void {
    if (this.hasVlfPermission) {
      this.isVlfStatusLoading = true;
      this.searchService
        .getVlfStatus()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.isVlfOptedIn = !!res?.status;
            this.isVlfStatusLoading = false;
            if (this.selectedTab?.value === LeadTabs.VLF && this.isVlfOptedIn) {
              this.triggerInitialData();
            }
          },
          error: () => {
            this.isVlfOptedIn = false;
            this.isVlfStatusLoading = false;
          },
        });
    } else {
      this.isVlfStatusLoading = false;
    }
  }

  toggleVlfInfo(): void {
    this.showVlfInfo = !this.showVlfInfo;
  }

  optInVlf(): void {
    this.isVlfActionLoading = true;
    this.searchService.updateVlfStatus(true).subscribe({
      next: () => {
        this.commonService.triggerToast({
          type: ToastType.Success,
          message: 'You have successfully opted in to the VLF Programme!',
        });
        this.isVlfOptedIn = true;
        this.isVlfActionLoading = false;
        this.triggerInitialData();
      },
      error: (err) => {
        this.isVlfActionLoading = false;
        this.commonService.triggerToast({
          type: ToastType.Error,
          message: err?.error?.detail || 'Failed to opt in to VLF Programme.',
        });
      },
    });
  }

  optOutVlf(): void {
    this.isVlfActionLoading = true;
    this.searchService.updateVlfStatus(false).subscribe({
      next: () => {
        this.commonService.triggerToast({
          type: ToastType.Success,
          message: 'You have successfully opted out of the VLF Programme.',
        });
        this.isVlfOptedIn = false;
        this.isVlfActionLoading = false;
        this.resetSearchResults();
      },
      error: (err) => {
        this.isVlfActionLoading = false;
        this.commonService.triggerToast({
          type: ToastType.Error,
          message: err?.error?.detail || 'Failed to opt out of VLF Programme.',
        });
      },
    });
  }
}
